
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";

export default function AddWebsite() {
  const [website, setWebsite] = useState({
    title: "",
    url: "",
    platform: "wordpress",
    backupFrequency: "daily",
    username: "",
    password: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setWebsite((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setWebsite((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    // This is a mock submission - in a real app you'd connect to a backend
    setTimeout(() => {
      setIsLoading(false);
      toast({
        title: "Website Added!",
        description: "Your website has been added and your first backup will start soon.",
      });
      navigate("/dashboard");
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isLoggedIn={true} />
      <div className="container mx-auto py-10 max-w-2xl px-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Add Website</CardTitle>
            <CardDescription>
              Configure your website for automated backups
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-6">
              {/* Website Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Website Details</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="title">Website Name</Label>
                  <Input
                    id="title"
                    name="title"
                    placeholder="My Awesome Website"
                    value={website.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="url">Website URL</Label>
                  <Input
                    id="url"
                    name="url"
                    placeholder="example.com"
                    value={website.url}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Platform</Label>
                  <RadioGroup 
                    value={website.platform} 
                    onValueChange={(value) => handleSelectChange("platform", value)}
                    className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="wordpress" id="wordpress" />
                      <Label htmlFor="wordpress">WordPress</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="shopify" id="shopify" />
                      <Label htmlFor="shopify">Shopify</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="woocommerce" id="woocommerce" />
                      <Label htmlFor="woocommerce">WooCommerce</Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>
              
              {/* Backup Settings */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="text-lg font-medium">Backup Settings</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="backupFrequency">Backup Frequency</Label>
                  <Select 
                    value={website.backupFrequency}
                    onValueChange={(value) => handleSelectChange("backupFrequency", value)}
                  >
                    <SelectTrigger id="backupFrequency">
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hourly">Hourly</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Authentication */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="text-lg font-medium">Authentication</h3>
                <p className="text-sm text-gray-500">
                  We need these credentials to access your website for backups.
                  Your credentials are encrypted and stored securely.
                </p>
                
                <div className="space-y-2">
                  <Label htmlFor="username">Username/API Key</Label>
                  <Input
                    id="username"
                    name="username"
                    value={website.username}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">Password/API Secret</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={website.password}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button type="button" variant="outline" onClick={() => navigate("/dashboard")}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Adding Website..." : "Add Website"}
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  );
}
